import { FC, useEffect } from "react";
import { useFormikContext } from "formik";
import {
  RadioInput,
  TextInput,
  ReferenceDropDown,
  TCInput,
  VknInput,
} from "../../../components";
import { Grid } from "@mui/material";
import useLocales from "../../../hooks/useLocales";
import { selectCustomerDisabledFields } from "../../../features/customer/customerSlice";
import { useAppSelector } from "../../../app/hooks";
import { ConditionProps } from "../../../interfaces/Customer";
import {
  customerKindEnum,
  customerKindOptions,
  customerTypeEnum,
  locale,
  onlyNumberKey,
} from "../../../utils";
import { CustomerStatusHandler } from "../common/CustomerStatusHandler";
import CustomerSuggest from "../../../components/inputs/CustomerSuggest";
import CustomerDateInput from "../common/CustomerDateInput";
import EConversionHandler from "../contact/common/EConversionHandler";

interface IGeneralCreateProps {
  setCondition: Function;
  setExistCustomer: Function;
}

const GeneralCreate: FC<IGeneralCreateProps> = ({
  setCondition,
  setExistCustomer,
}) => {
  const { translate } = useLocales();
  const formik: any = useFormikContext();
  const disabledFields = useAppSelector(selectCustomerDisabledFields);
  const customerTypeId: number = formik.values.customerTypeId;
  const isForeign: boolean = formik?.values?.customerDetail?.isForeign;
  const name: string = formik.values.name;
  const surname: string = formik.values.surname;
  const isExistCustomer: boolean = formik.values.isExistCustomer;

  // if customer is real adding address step
  // if customer is foreign remove contact validation
  useEffect(() => {
    setExistCustomer(formik.values.isExistCustomer);
    if (Number(customerTypeId) === customerTypeEnum.real) {
      setCondition((prevState: ConditionProps) => ({
        ...prevState,
        customerTypeId: customerTypeEnum.real,
      }));
    } else {
      setCondition((prevState: ConditionProps) => ({
        ...prevState,
        customerTypeId: customerTypeEnum.lead,
      }));
    }
    if (isForeign) {
      setCondition((prevState: ConditionProps) => ({
        ...prevState,
        isForeign: true,
      }));
      formik.setFieldValue("contact.nationalityId", "", false);

      formik.setFieldValue("customerDetail.isTaxFree", true, true);

      formik.setFieldValue("address.isForeign", true, true);
    } else {
      setCondition((prevState: ConditionProps) => ({
        ...prevState,
        isForeign: false,
      }));
      formik.setFieldValue(
        "contact.nationalityId",
        formik.initialValues.contact.nationalityId,
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerTypeId, isForeign, setCondition, formik.values]);

  useEffect(() => {
    if (Number(customerTypeId) !== customerTypeEnum.real) {
      if (name && name !== "")
        formik.setFieldValue("contact.name", name, false);
      if (surname && surname !== "")
        formik.setFieldValue("contact.surname", surname, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerTypeId, name, surname]);

  return (
    <Grid container spacing={3}>
      <>
        <EConversionHandler setCondition={setCondition} />
        <CustomerStatusHandler />
        <SetCitizenNumberInContact />
        <Grid item md={12} sm={12} xs={12} lg={12}>
          <RadioInput
            name="customerKindId"
            label={translate("resources.customers.customerKind")}
            radioGroup={locale(customerKindOptions, "resources.customers.")}
            disabled={formik.values.isExistCustomer}
          />
        </Grid>

        {Number(formik.values?.customerKindId) === customerKindEnum.indivual ? (
          <>
            {Number(formik.values.customerTypeId) !== customerTypeEnum.lead && (
              <Grid item md={6} lg={6} xs={12} sm={12}>
                <TCInput
                  name="citizenOrTaxNumber"
                  label={translate("resources.customers.tckn")}
                  onKeyPressCapture={(event: any) => onlyNumberKey(event)}
                  disabled={isForeign}
                />
              </Grid>
            )}
            <Grid item md={6} lg={6} xs={12} sm={12}>
              {Number(formik.values.customerTypeId) ===
              customerTypeEnum.real ? (
                <TextInput
                  name="name"
                  label={translate("resources.customers.name")}
                  disabled={formik.values.isExistCustomer}
                />
              ) : (
                <CustomerSuggest
                  name="name"
                  label={translate("resources.customers.name")}
                  reference="fixity/customer/customer/get-customer-used-name-list"
                  optionName="usedName"
                  disabled={formik.values.isExistCustomer}
                />
              )}
            </Grid>
            <Grid item md={6} lg={6} xs={12} sm={12}>
              <TextInput
                name="surname"
                label={translate("resources.customers.surname")}
                disabled={formik.values.isExistCustomer}
              />
            </Grid>
            <CustomerDateInput />
          </>
        ) : (
          <>
            {Number(formik?.values.customerKindId) ===
            customerKindEnum.soleProprietorship ? (
              <Grid item md={6} lg={6} xs={12} sm={12}>
                <TCInput
                  name="citizenOrTaxNumber"
                  label={translate("resources.customers.tckn")}
                  onKeyPressCapture={(event: any) => onlyNumberKey(event)}
                  disabled={isForeign}
                />
              </Grid>
            ) : (
              <Grid item md={6} lg={6} xs={12} sm={12}>
                <VknInput
                  name="citizenOrTaxNumber"
                  label={translate("resources.customers.vkn")}
                  onKeyPressCapture={(event: any) => onlyNumberKey(event)}
                  disabled={isForeign}
                />
              </Grid>
            )}
            <Grid item md={6} lg={6} xs={12} sm={12}>
              {Number(formik.values.customerTypeId) ===
              customerTypeEnum.real ? (
                <TextInput
                  name="commercialTitle"
                  label={translate("resources.customers.commercialTitle")}
                  disabled={formik.values.isExistCustomer}
                />
              ) : (
                <CustomerSuggest
                  name="commercialTitle"
                  label={translate("resources.customers.commercialTitle")}
                  reference="fixity/customer/customer/get-customer-used-name-list"
                  optionName="usedName"
                  disabled={formik.values.isExistCustomer}
                />
              )}
            </Grid>
            <Grid item md={6} lg={6} xs={12} sm={12}>
              <TextInput
                name="alias"
                label={translate("resources.customers.alias")}
                disabled={formik.values.isExistCustomer}
              />
            </Grid>
            <Grid item md={6} lg={6} xs={12} sm={12}>
              <ReferenceDropDown
                name="customerDetail.taxOfficeId"
                label={translate("resources.customers.tax_office")}
                reference="fixity/customer/taxOffice"
                optionName="name"
                disabled={formik.values.isExistCustomer}
              />
            </Grid>
          </>
        )}
      </>
    </Grid>
  );
};

const SetCitizenNumberInContact = () => {
  const { values, setFieldValue }: any = useFormikContext();
  useEffect(() => {
    if (values.customerKindId === customerKindEnum.indivual) {
      setFieldValue("contact.identityNo", values.citizenOrTaxNumber, false);
      setFieldValue(
        "contact.dateOfBirth",
        values?.dateOfBirth ? values?.dateOfBirth : "",
        false
      );
    } else {
      setFieldValue("contact.identityNo", "", false);
      setFieldValue("contact.dateOfBirth", "", false);
    }
  }, [values.customerKindId, values.citizenOrTaxNumber, values.dateOfBirth]);
  return null;
};

export default GeneralCreate;
